import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { CatalogBrowser } from "@/components/master-catalog/CatalogBrowser";
import { SelectionPanel } from "@/components/master-catalog/SelectionPanel";
import { partnerCatalogApi, newRequestId, downloadBlob } from "@/components/master-catalog/mcApi";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PinGate({ token, onOk }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const submit = async () => {
    setErr("");
    try {
      const { data } = await axios.post(`${BASE}/mc/${token}/verify`, { pin });
      sessionStorage.setItem(`mc_s_${token}`, data.session);
      onOk(data.session);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(d?.error === "pin_locked" ? "Too many attempts — try again in 10 minutes" : e.response?.status === 404 ? "Link not available" : "Incorrect PIN");
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF8F3] p-6">
      <div className="luxe-card p-8 w-full max-w-sm space-y-4" data-testid="mc-pin-gate">
        <h1 className="font-serif text-3xl text-[#0D1B2A] flex items-center gap-2"><Lock className="w-6 h-6" /> Master Catalog</h1>
        <p className="text-sm text-slate-500">Enter the PIN shared with you by Ashth.</p>
        <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric"
          onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="PIN" className="text-2xl tracking-[0.4em] text-center h-14" data-testid="mc-pin-input" />
        {err && <div className="text-sm text-red-600" data-testid="mc-pin-error">{err}</div>}
        <Button onClick={submit} disabled={pin.length < 4} className="w-full bg-[#0D1B2A] text-white" data-testid="mc-pin-submit">Open catalog</Button>
      </div>
    </div>
  );
}

export default function MasterCatalogPartner() {
  const { token } = useParams();
  const [session, setSession] = useState(() => sessionStorage.getItem(`mc_s_${token}`));
  const [sel, setSel] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const api = useMemo(() => (session ? partnerCatalogApi(token, session) : null), [token, session]);

  const handleErr = useCallback((e) => {
    if (e.response?.status === 401) { sessionStorage.removeItem(`mc_s_${token}`); setSession(null); return; }
    if (e.response?.status === 409) toast.error("This line changed on another device — refreshed to the latest");
    else toast.error("Could not save — check your connection and retry");
  }, [token]);

  useEffect(() => { if (api) api.selection().then(setSel).catch(handleErr); }, [api, handleErr]);

  const run = async (body) => {
    setSaveState("saving");
    const op = { request_id: newRequestId(), ...body };
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const data = await api.op(op);
        setSel(data);
        setSaveState("saved");
        return true;
      } catch (e) {
        if (e.response || attempt === 1) {
          setSaveState(e.response?.status === 409 ? "saved" : "error");
          handleErr(e);
          if (e.response?.status === 409) api.selection().then(setSel);
          return false;
        }
      }
    }
    return false;
  };

  const exportFile = async (fmt) => {
    try {
      await downloadBlob(api.exportUrl(fmt, sel), `MasterCatalog_${sel.link.name}.${fmt}`);
    } catch (e) {
      if (e.response?.status === 409) { toast.error("Selection or prices changed — refreshed, download again"); api.selection().then(setSel); }
      else toast.error("Download failed");
    }
  };

  if (!session) return <PinGate token={token} onOk={setSession} />;
  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      <header className="px-6 py-5 border-b border-[#0D1B2A]/10 bg-white">
        <h1 className="font-serif text-3xl text-[#0D1B2A]" data-testid="mc-partner-title">Ashth Master Catalog</h1>
        <p className="text-sm text-slate-500">{sel?.link?.name}</p>
      </header>
      <main className="p-4 md:p-6 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] gap-6 items-start">
        <div className="min-w-0"><CatalogBrowser api={api} onAdd={async (b) => { if (await run({ op: "add", ...b })) toast.success("Added to selection"); }} /></div>
        <div className="xl:sticky xl:top-4 min-w-0">
          <SelectionPanel sel={sel} saveState={saveState}
            onQty={(ln, qty) => run({ op: "set_qty", line_id: ln.id, line_rev: ln.rev, qty })}
            onRemove={(ln) => run({ op: "remove", line_id: ln.id, line_rev: ln.rev })}
            onExport={exportFile} />
        </div>
      </main>
    </div>
  );
}
